import React from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Copy, ArrowSquareOut, Funnel, X } from "@phosphor-icons/react";

const fetcher = (u) => api.get(u).then(r => r.data);

const platforms = ["Facebook_Ads", "Instagram_Ads", "LinkedIn_Ads", "TikTok_Ads", "Facebook_Organic", "LinkedIn_DM"];
const sectors = ["FHA", "ITIN", "VA", "Conventional", "DSCR", "Jumbo", "Refi"];
const intents = ["First_Time_Buyer", "Refinance", "Investor_DSCR", "Cash_Out", "Pre_Approval"];

function NewLeadModal({ open, onClose, onCreated }) {
  const [form, setForm] = React.useState({
    source_platform: "Facebook_Ads",
    campaign_id: "fha_memphis_2026",
    ad_creative_variant: "story_vs_stat_04",
    hook_sector: "FHA",
    self_reported_intent: "First_Time_Buyer",
    estimated_timeline_days: 60,
    initial_chat_engagement_score: 0.75,
    contact_name: "",
    contact_handle: "",
  });
  const [loading, setLoading] = React.useState(false);

  if (!open) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post("/leads", {
        ...form,
        estimated_timeline_days: parseInt(form.estimated_timeline_days),
        initial_chat_engagement_score: parseFloat(form.initial_chat_engagement_score),
      });
      toast.success(`Lead ${r.data.social_lead_id} captured`);
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="new-lead-modal">
      <div className="w-full max-w-2xl border border-white/[0.1] bg-[#0a0a0a] rounded-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-muted-foreground">Capture · new social lead</div>
            <h3 className="mt-1 font-heading font-black text-2xl tracking-tighter">Inject payload</h3>
          </div>
          <button data-testid="close-modal" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Source platform</label>
            <select data-testid="lead-platform" value={form.source_platform} onChange={set("source_platform")} className="w-full bg-[#0a0a0a] border border-white/10 focus:border-primary/50 outline-none px-3 py-2 rounded-sm text-sm">
              {platforms.map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Hook sector</label>
            <select data-testid="lead-sector" value={form.hook_sector} onChange={set("hook_sector")} className="w-full bg-[#0a0a0a] border border-white/10 focus:border-primary/50 outline-none px-3 py-2 rounded-sm text-sm">
              {sectors.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Campaign ID</label>
            <input data-testid="lead-campaign" value={form.campaign_id} onChange={set("campaign_id")} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-3 py-2 rounded-sm text-sm" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Ad variant</label>
            <input data-testid="lead-variant" value={form.ad_creative_variant} onChange={set("ad_creative_variant")} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-3 py-2 rounded-sm text-sm" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Self-reported intent</label>
            <select data-testid="lead-intent" value={form.self_reported_intent} onChange={set("self_reported_intent")} className="w-full bg-[#0a0a0a] border border-white/10 focus:border-primary/50 outline-none px-3 py-2 rounded-sm text-sm">
              {intents.map((i) => <option key={i}>{i}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Timeline (days)</label>
            <input data-testid="lead-timeline" type="number" min={0} value={form.estimated_timeline_days} onChange={set("estimated_timeline_days")} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-3 py-2 rounded-sm text-sm" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Engagement score (0-1)</label>
            <input data-testid="lead-engagement" type="number" step="0.01" min={0} max={1} value={form.initial_chat_engagement_score} onChange={set("initial_chat_engagement_score")} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-3 py-2 rounded-sm text-sm" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Contact name</label>
            <input data-testid="lead-name" value={form.contact_name} onChange={set("contact_name")} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-3 py-2 rounded-sm text-sm" placeholder="Optional" />
          </div>
          <div className="col-span-2">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Contact handle</label>
            <input data-testid="lead-handle" value={form.contact_handle} onChange={set("contact_handle")} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-3 py-2 rounded-sm text-sm" placeholder="@handle or messenger id" />
          </div>
        </div>

        <button onClick={submit} disabled={loading} data-testid="lead-submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-sm font-medium primary-glow hover-lift text-sm disabled:opacity-60">
          {loading ? "Capturing…" : "Capture & generate handoff payload"}
        </button>
      </div>
    </div>
  );
}

export default function LeadsPipeline() {
  const { data: leads, mutate } = useSWR("/leads", fetcher);
  const [open, setOpen] = React.useState(false);
  const [filter, setFilter] = React.useState("all");

  const filtered = (leads || []).filter((l) => {
    if (filter === "all") return true;
    if (filter === "synced") return l.handoff_state.mainSiteSyncComplete;
    if (filter === "pending") return !l.handoff_state.mainSiteSyncComplete;
    return true;
  });

  const copyToken = (t) => { navigator.clipboard.writeText(t); toast.success("Payload token copied"); };

  const syncLead = async (id) => {
    try {
      await api.post(`/leads/${id}/sync`);
      toast.success("Handoff synced");
      mutate();
    } catch (err) {
      toast.error("Sync failed");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-muted-foreground">Pipeline · Social leads</div>
          <h1 className="mt-2 font-heading font-black text-4xl tracking-tighter">Unified Social Pipeline</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Every social touch, structured into the DW schema. Encrypted payload tokens carry attribution and pre-sort answers
            straight into the main lending intake.
          </p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="btn-new-lead" className="inline-flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground rounded-sm font-medium primary-glow hover-lift text-sm">
          <Plus size={14} weight="bold" /> Capture lead
        </button>
      </div>

      <div className="mt-6 flex items-center gap-2 text-xs font-mono">
        <Funnel size={14} className="text-muted-foreground" />
        {[
          { k: "all", l: "ALL" },
          { k: "pending", l: "PENDING" },
          { k: "synced", l: "SYNCED" },
        ].map((f) => (
          <button key={f.k} data-testid={`filter-${f.k}`} onClick={() => setFilter(f.k)} className={`px-3 py-1 rounded-sm border ${filter === f.k ? "border-primary text-primary" : "border-white/10 text-muted-foreground hover:text-foreground"}`}>
            {f.l}
          </button>
        ))}
        <div className="ml-auto text-muted-foreground">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</div>
      </div>

      <div className="mt-4 border border-white/[0.08] bg-[#0a0a0a] rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono min-w-[1100px]">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr className="border-b border-white/[0.08]">
                <th className="text-left py-3 px-4">socialLeadId</th>
                <th className="text-left py-3 px-4">Attribution</th>
                <th className="text-left py-3 px-4">Pre-sort</th>
                <th className="text-left py-3 px-4">Payload Token</th>
                <th className="text-left py-3 px-4">State</th>
                <th className="text-left py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">No leads match this filter.</td></tr>
              )}
              {filtered.map((l) => (
                <tr key={l.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] align-top">
                  <td className="py-3 px-4 whitespace-nowrap">
                    <div className="text-primary">{l.social_lead_id}</div>
                    {l.contact_name && <div className="text-[10px] text-muted-foreground mt-0.5 normal-case">{l.contact_name}</div>}
                    {l.contact_handle && <div className="text-[10px] text-muted-foreground normal-case">{l.contact_handle}</div>}
                  </td>
                  <td className="py-3 px-4 text-[11px]">
                    <div><span className="text-muted-foreground">src:</span> {l.marketing_attribution.sourcePlatform}</div>
                    <div><span className="text-muted-foreground">cmp:</span> {l.marketing_attribution.campaignId}</div>
                    <div><span className="text-muted-foreground">var:</span> {l.marketing_attribution.adCreativeVariant}</div>
                    <div><span className="text-muted-foreground">sec:</span> <span className="text-primary">{l.marketing_attribution.hookSector}</span></div>
                  </td>
                  <td className="py-3 px-4 text-[11px]">
                    <div><span className="text-muted-foreground">intent:</span> {l.pre_sort_metrics.selfReportedIntent}</div>
                    <div><span className="text-muted-foreground">time:</span> {l.pre_sort_metrics.estimatedTimelineDays}d</div>
                    <div><span className="text-muted-foreground">engage:</span> <span className="text-success">{(l.pre_sort_metrics.initialChatEngagementScore * 100).toFixed(0)}%</span></div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2 max-w-[220px]">
                      <span className="truncate text-[10px] text-muted-foreground border border-white/10 px-2 py-1 rounded-sm">{l.handoff_state.securePayloadToken.slice(0, 22)}…</span>
                      <button data-testid={`copy-token-${l.id}`} onClick={() => copyToken(l.handoff_state.securePayloadToken)} className="text-muted-foreground hover:text-primary"><Copy size={12} /></button>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">{new Date(l.handoff_state.redirectTimestamp).toLocaleString()}</div>
                  </td>
                  <td className="py-3 px-4">
                    {l.handoff_state.mainSiteSyncComplete ? (
                      <span className="px-2 py-1 text-[10px] text-success border border-success/40 rounded-sm">SYNCED</span>
                    ) : (
                      <span className="px-2 py-1 text-[10px] text-muted-foreground border border-white/10 rounded-sm">PENDING</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <a href={`/handoff/${l.handoff_state.securePayloadToken}`} target="_blank" rel="noreferrer" data-testid={`open-handoff-${l.id}`} className="inline-flex items-center gap-1 text-[10px] border border-white/10 px-2 py-1 rounded-sm hover:border-primary/40 hover:text-primary">
                        <ArrowSquareOut size={11} /> HANDOFF
                      </a>
                      {!l.handoff_state.mainSiteSyncComplete && (
                        <button data-testid={`sync-${l.id}`} onClick={() => syncLead(l.id)} className="text-[10px] border border-primary/40 text-primary px-2 py-1 rounded-sm hover:bg-primary/10">SYNC</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <NewLeadModal open={open} onClose={() => setOpen(false)} onCreated={mutate} />
    </div>
  );
}
